import { useCallback, useEffect, useState } from "react";
import api, { getErrorMessage } from "@/lib/api";
import { toast } from "sonner";

// Reminders are kept in the application only — nothing is e-mailed — so the screens say "diingatkan di
// portal", never "terkirim". Overdue comes from the server (due date and the reminder's own state).
const SUBJECTS = ["Laporan Auditor Komponen", "Ringkasan Salah Saji", "Hal Kecurangan", "Kelangsungan Usaha",
  "Peristiwa Kemudian", "Bukti Komponen", "Lainnya", "Status alur kerja", "Tanggapan catatan reviu"];

const rows = (data) => (Array.isArray(data) ? data.filter((r) => r && r.id && r.due_date) : []);

function Row({ r, action }) {
  return (
    <li className={`border rounded-sm px-2 py-1.5 text-xs ${r.overdue ? "border-amber-400 bg-amber-50" : "border-border bg-white"}`}
        data-testid={`reminder-${r.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium">{r.subject}{r.entity ? ` · ${r.entity}` : ""}</span>
        <span className="tabular-nums">jatuh tempo {r.due_date}{r.overdue ? " · TERLAMBAT" : ""}{r.status === "CLOSED" ? " · ditutup" : ""}</span>
      </div>
      <p className="mt-0.5 whitespace-pre-line">{r.message}</p>
      <div className="mt-0.5 text-muted-foreground">
        {r.acknowledged_at ? `Dikonfirmasi ${r.acknowledged_by_name || ""}` : "Belum dikonfirmasi auditor komponen"}
        {r.close_reason ? ` · Alasan penutupan: ${r.close_reason}` : ""}
      </div>
      {action}
    </li>
  );
}

/** The component auditor's view: reminders on this component, acknowledged one by one. */
export function PortalReminders({ cid }) {
  const [list, setList] = useState([]);
  const load = useCallback(() => {
    api.get(`/portal/components/${cid}/reminders`).then((r) => setList(rows(r.data))).catch(() => setList([]));
  }, [cid]);
  useEffect(() => { load(); }, [load]);
  if (list.length === 0) return null;
  const acknowledge = async (rid) => {
    try { await api.post(`/portal/components/${cid}/reminders/${rid}/acknowledge`); toast.success("Pengingat dikonfirmasi"); load(); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };
  return (
    <ul className="space-y-1.5" data-testid="portal-reminders">
      {list.map((r) => (
        <Row key={r.id} r={r} action={r.status === "OPEN" && !r.acknowledged_at && (
          <button type="button" className="mt-1 border rounded px-2 py-0.5 hover:bg-muted" onClick={() => acknowledge(r.id)}
                  data-testid={`reminder-ack-${r.id}`}>Konfirmasi diterima</button>)} />
      ))}
    </ul>
  );
}

/** The group team's view: set a dated reminder on a component, see what is overdue, close with a reason. */
export function GroupReminders({ eid, components }) {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ component_id: "", subject: SUBJECTS[0], due_date: "", message: "" });
  const load = useCallback(() => {
    api.get(`/engagements/${eid}/component-reminders`).then((r) => setList(rows(r.data))).catch(() => setList([]));
  }, [eid]);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!form.component_id) return toast.error("Pilih komponen.");
    try {
      await api.post(`/engagements/${eid}/components/${form.component_id}/reminders`, form);
      toast.success("Pengingat dicatat di portal auditor komponen");
      setForm({ ...form, message: "" }); load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };
  const close = async (r) => {
    const reason = window.prompt("Alasan penutupan pengingat:"); if (!reason) return;
    try { await api.post(`/engagements/${eid}/components/${r.component_id}/reminders/${r.id}/close`, { reason }); load(); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };
  const overdue = list.filter((r) => r.overdue).length;
  return (
    <div className="space-y-2" data-testid="group-reminders">
      <div className="text-sm font-semibold">Pengingat Auditor Komponen {overdue > 0 && <span className="text-amber-800" data-testid="group-reminders-overdue">· {overdue} terlambat</span>}</div>
      <p className="text-xs text-muted-foreground">Pengingat ditampilkan di portal auditor komponen; sistem tidak mengirim e-mail.</p>
      <div className="flex flex-wrap gap-2 text-xs">
        <select aria-label="Komponen" className="border rounded h-8 px-1 bg-white" value={form.component_id}
                onChange={(e) => setForm({ ...form, component_id: e.target.value })} data-testid="reminder-component">
          <option value="">Pilih komponen</option>
          {(components || []).map((c) => <option key={c.id} value={c.id}>{c.entity || c.id}</option>)}
        </select>
        <select aria-label="Hal" className="border rounded h-8 px-1 bg-white" value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}>
          {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input aria-label="Jatuh tempo" type="date" className="border rounded h-8 px-1" value={form.due_date}
               onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="reminder-due" />
        <input aria-label="Isi pengingat" className="border rounded h-8 px-1 flex-1 min-w-40" placeholder="Apa yang diminta"
               value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="reminder-message" />
        <button type="button" className="border rounded px-2 h-8 hover:bg-muted" onClick={create} data-testid="reminder-create">Catat pengingat</button>
      </div>
      {list.length > 0 && (
        <ul className="space-y-1.5" data-testid="group-reminders-list">
          {list.map((r) => (
            <Row key={r.id} r={r} action={r.status === "OPEN" && (
              <button type="button" className="mt-1 border rounded px-2 py-0.5 hover:bg-muted" onClick={() => close(r)}
                      data-testid={`reminder-close-${r.id}`}>Tutup</button>)} />
          ))}
        </ul>
      )}
    </div>
  );
}
